import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { codedError, extractFunctionError, settledRefusalOrNull } from "@/utils/errorUtils";
import { handleError } from '@/utils/handleError';
import {
  CreatePaymentRequest,
  CreatePaymentResponse,
  PaymentScaStatusResponse
} from "@/types/openpayments/paymentInitiation";
import { Invoice, TransactionSuggestion } from "@/types/payments";
import { CreatePayableRequest, UpdatePayableRequest, Payable } from "@/types/payable";
import { translateOutsideReact } from '@/i18n/translateOutsideReact';

// caller-access-context.EC5/AC3 (issue #616) — see accountApiClient.ts for the rationale.
const CALLER_ACCESS_MODE_HEADERS = { "x-caller-access-mode": "user-present" } as const;

/**
 * A structured error body (`{ error_code, message, details }`) returned in `data` becomes a real
 * Error carrying the same fields, so callers keep reading `error_code` and nothing throws a plain
 * object (FIN-260 P3).
 */
function structuredBodyError(body: { error_code: string; message?: string }): Error & typeof body {
  return Object.assign(new Error(body.message ?? body.error_code), body);
}

/** updatePayable refusals whose server message is shown to the user as-is (FIN-968). */
const UPDATE_PAYABLE_REFUSALS: ReadonlySet<unknown> = new Set([
  "PAYMENT_ALREADY_SIGNED", "PAYMENT_STARTED_FROM_OTHER_ACCOUNT", "CONFLICT",
]);

/**
 * Client for the Payment API edge function
 */
export class PaymentApiClient {
  /**
   * Initiate a domestic payment
   */
  async createPayment(payload: CreatePaymentRequest): Promise<CreatePaymentResponse> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "createPayment",
          payload
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw await toEdgeFunctionFailure(error, 'Error creating payment');
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCreatePayment'), 'createPayment');
      throw error;
    }
  }

  async getPaymentAuthorisationScaStatus(
    paymentId: string,
    authorisationId: string
  ): Promise<PaymentScaStatusResponse> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "getPaymentAuthorisationScaStatus",
          paymentId,
          authorisationId
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw await toEdgeFunctionFailure(error, 'Error getting SCA status');
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetScaStatus'), 'getPaymentAuthorisationScaStatus');
      throw error;
    }
  }

  async getAuthorisationStatus(
    signingBasketId: string,
    authorisationId: string,
    xRequestId: string,
    bicFi: string,
    /** A money transfer's payments have no payable, so they never need review (FIN-968). */
    signingFor: 'payables' | 'transfer' = 'payables',
  ): Promise<{ completed: boolean, needsReview?: boolean, scaStatus: string, message?: string, qrCodeImage?: string, qrToken?: string, bankIdUrl?: string, scaRedirect?: string, refreshInterval?: number }> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "getAuthorisationStatus",
          signingBasketId,
          authorisationId,
          xRequestId,
          bicFi,
          signingFor,
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) throw await toEdgeFunctionFailure(error, 'Error getting authorisation status');
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToGetAuthorisationStatus'), 'getAuthorisationStatus');
      throw error;
    }
  }

  async getPayables(): Promise<Invoice[]> {
    try {
      console.log("PaymentApiClient: Fetching payables");
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: { action: "getPayables" },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) {
        throw await toEdgeFunctionFailure(error, 'Error fetching payables');
      }
      
      if (!data) {
        console.warn("No data returned from getPayables endpoint");
        return [];
      }
      
      console.log("PaymentApiClient: Payables fetch successful", data);
      return data as Invoice[];
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchPayables'), 'getPayables');
      throw error;
    }
  }

  async getPendingPayablesBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "getPendingPayablesBalance",
          currency: targetCurrency
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching pending payables balance');
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchPendingPayablesBalance'), 'getPendingPayablesBalance');
      throw error;
    }
  }

  /**
   * Create a new payable record
   */
  async createPayable(request: CreatePayableRequest): Promise<Payable> {
    try {
      console.log("PaymentApiClient: Creating payable", request);
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "createPayable",
          ...request
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      
      if (error) {
        throw await toEdgeFunctionFailure(error, 'Error creating payable');
      }
      
      console.log("PaymentApiClient: Payable created successfully", data);
      return data as Payable;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToCreatePayable'), 'createPayable');
      throw error;
    }
  }

  /**
   * Update an existing payable record
   */
  async updatePayable(request: UpdatePayableRequest): Promise<Payable> {
    console.log("PaymentApiClient: Updating payable", request);
    const { data, error } = await supabase.functions.invoke("payment-api", {
      body: {
        action: "updatePayable",
        ...request
      },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });

    if (error) {
      const body = await extractFunctionError(error);
      // FIN-968: a refused source-account change is named by the server; the caller shows it.
      if (UPDATE_PAYABLE_REFUSALS.has(body?.error) && body?.message) {
        throw Object.assign(new Error(body.message), { error: body.error });
      }
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToUpdatePayable'), 'updatePayable', body);
      throw await toEdgeFunctionFailure(error, translateOutsideReact('consultantErrors.fallback.failedToUpdatePayable'), body);
    }

    console.log("PaymentApiClient: Payable updated successfully", data);
    return data as Payable;
  }

  /**
   * Update payable status (for manual handling, etc.)
   */
  async updatePayableStatus(id: string, status: string, paymentDate?: string, isInvoiceId: boolean = false, actualSekAmount?: number): Promise<Payable> {
    try {
      console.log("PaymentApiClient: Updating payable status", { id, status, paymentDate, isInvoiceId, actualSekAmount });
      const body: any = {
        action: "updatePayableStatus",
        status,
        payment_date: paymentDate,
        actualSekAmount
      };

      // Set either id or invoice_id based on the isInvoiceId flag
      if (isInvoiceId) {
        body.invoice_id = id;
      } else {
        body.id = id;
      }

      const { data, error } = await supabase.functions.invoke("payment-api", {
        body,
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) {
        throw await toEdgeFunctionFailure(error, 'Error updating payable status');
      }

      console.log("PaymentApiClient: Payable status updated successfully", data);
      return data as Payable;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToUpdatePayableStatus'), 'updatePayableStatus');
      throw error;
    }
  }

  /**
   * Precomputed payable→transaction match suggestions (issue #462) — reads the async
   * match_jobs pipeline's already-computed auto_match_decisions suggestion. Unlike the
   * receivable/receipt/sales-report equivalents, payables have no JIT scoring path at all, so
   * there is no fallback call here — an empty result means "nothing computed yet" and the
   * caller (PayableMatchSuggestionsModal) falls back to its own plain date-picker UI.
   */
  async getPrecomputedTransactionSuggestionsForPayable(payableId: string): Promise<TransactionSuggestion[]> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: { action: "getPrecomputedTransactionSuggestionsForPayable", payableId },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) {
        throw await toEdgeFunctionFailure(error, 'Error fetching precomputed transaction suggestions for payable');
      }

      return (data as TransactionSuggestion[]) || [];
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToFetchTransactionSuggestions'), 'getPrecomputedTransactionSuggestionsForPayable');
      throw error;
    }
  }

  /**
   * Confirm a manual payable→transaction match (issue #462). The backend derives the
   * confirming user from the authenticated session — never pass a userId here. Can throw a
   * 409 (ConflictError, already matched) or 404 (NotFoundError, stale/invalid transactionId).
   * FIN-968: a transaction that already settles another document is a named refusal the caller
   * shows itself; anything else is reported and toasted here via handleError().
   */
  async confirmPayableMatch(payableId: string, transactionId: string): Promise<{ decisionId: string }> {
    const { data, error } = await supabase.functions.invoke("payment-api", {
      body: { action: "confirmPayableMatch", payableId, transactionId },
      headers: CALLER_ACCESS_MODE_HEADERS,
    });

    if (error) {
      const { settled, body } = await settledRefusalOrNull(error);
      if (settled) throw settled;
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToConfirmMatch'), 'confirmPayableMatch', body);
      throw error;
    }

    return data as { decisionId: string };
  }

  /**
   * Acknowledge a payable as paid externally when a bank account is connected but no matching
   * transaction was found (issue #498, design plan's case c — "unmatched acknowledged"). The
   * backend derives the confirming user from the authenticated session — never pass a userId
   * here. Mirrors confirmPayableMatch's error-surfacing convention (handleError below reports
   * failures; the caller's own try/catch only manages its own UI state).
   */
  async acknowledgeExternalPayment(payableId: string, paymentDate: string, actualSekAmount?: number): Promise<{ decisionId: string }> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: { action: "acknowledgeExternalPayment", payableId, paymentDate, actualSekAmount },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      if (error) {
        throw await toEdgeFunctionFailure(error, 'Error acknowledging external payment');
      }

      return data as { decisionId: string };
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToAcknowledgeExternalPayment'), 'acknowledgeExternalPayment');
      throw error;
    }
  }

  /**
   * Pay selected invoices by their IDs
   */
  async paySelected(payableIds: string[], sameDevice: boolean = true): Promise<{ url?: string, signingBasketId: string, authorisationId: string, authMethod?: string, qrCodeImage?: string, qrToken?: string, bankIdUrl?: string, scaRedirect?: string, refreshInterval?: number, bicFi: string }> {
    try {
      const callbackUrl = `${window.location.origin}/bank`
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "paySelected",
          payableIds,
          sameDevice,
          callbackUrl,
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });

      console.log("Payment API response:", { data, error });

      // Check if the data contains an error (some edge functions return errors in data)
      if (data?.error_code) {
        console.log("Structured error found in data:", data);
        throw structuredBodyError(data);
      }

      if (error) {
        // Don't handle the error here - let it fall through to the catch block
        // where extractFunctionError can properly parse it
        throw error;
      }

      return data;
    } catch (error) {
      // Extract actual error from FunctionsHttpError
      const functionError = await extractFunctionError(error);
      if (functionError) {
        console.log('Extracted function error:', functionError);

        // Handle KYC_REQUIRED errors
        if (functionError.error_code === 'KYC_REQUIRED') {
          const kycError = new Error('KYC_REQUIRED');
          (kycError as any).error_code = 'KYC_REQUIRED';
          (kycError as any).details = functionError.details;
          throw kycError;
        }

        // PROVIDER_CAPABILITY_UNSUPPORTED (issue #826): this response shape (`error_code`, not
        // `error` — see payment-api/index.ts's onError) never reaches the `functionError.error`
        // branch below, so it needs its own detection here. Unlike before, `.message` is already
        // curated client-safe copy (`ProviderCapabilityError`'s constructor now builds it from
        // `capabilityUnsupportedCopy()` — see errors.ts/providerErrorCopy.ts) — no frontend-side
        // curation needed, just pass it through.
        if (functionError.error_code === 'PROVIDER_CAPABILITY_UNSUPPORTED') {
          throw new Error(functionError.message);
        }

        // CONSENT_REQUIRED (FIN-968): a source account has no bank access of its own. Kept coded,
        // with the accounts it names, so Confirm Payments can show Access needed for them.
        if (functionError.error === 'CONSENT_REQUIRED') {
          throw codedError('CONSENT_REQUIRED', functionError.details);
        }

        // Handle general errors from the edge function. Prefer `message` (the real,
        // specific reason -- e.g. a ZTL business rejection embedded in payment-api's
        // handleError() 500 body) over `error` (just the generic code, e.g.
        // "INTERNAL_ERROR") -- discovered while tracing what a real user actually sees in
        // PaymentAuthFlow's error Alert: this used to always show the generic code only,
        // discarding the detailed message that was already present in the response body.
        if (functionError.error) {
          throw new Error(functionError.message || functionError.error);
        }
      }

      // Re-throw KYC_REQUIRED errors
      if (error?.error_code === 'KYC_REQUIRED' || error?.message === 'KYC_REQUIRED') {
        throw error;
      }

      // PROVIDER_CAPABILITY_UNSUPPORTED (issue #826) can also reach here directly — when
      // Supabase returns the structured error in `data` rather than as a FunctionsHttpError,
      // the `data?.error_code` branch above re-throws that plain object as-is, which
      // extractFunctionError (FunctionsHttpError-only) can't parse, so `functionError` above
      // is null and the branch above never runs. Same mirrored direct check as KYC_REQUIRED
      // just above, for the same reason — `.message` is already curated (see the comment above).
      if (error?.error_code === 'PROVIDER_CAPABILITY_UNSUPPORTED') {
        throw new Error(error.message);
      }

      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToProcessPayment'), 'paySelected');
      throw error;
    }
  }

  /**
   * Transfer money between accounts
   */
  async transferMoney({ fromAccountId, toAccountId, amount, currency }: { fromAccountId: string, toAccountId: string, amount: number, currency: string }): Promise<{ url: string, signingBasketId: string, authorisationId: string }> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "transferMoney",
          fromAccountId,
          toAccountId,
          amount,
          currency
        },
        headers: CALLER_ACCESS_MODE_HEADERS,
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error transferring money');
      return data;
    } catch (error) {
      await handleError(error, translateOutsideReact('consultantErrors.fallback.failedToTransferMoney'), 'transferMoney');
      throw error;
    }
  }
}
